import { useSnackbar } from "notistack";
import { useCallback, useEffect, useRef, useState } from "react";
import { CachePolicies, Res } from "use-http";

import { HistogramEntry } from "components/Histogram";
import { JANZU_URL } from "config/config";
import { MAX_RETRIES, NO_RETRIES } from "config/constants";
import { useAppSelector } from "hooks/store";
import { useFetchCustom } from "hooks/useFetchCustom";
import { usePostEvent } from "hooks/usePostEvent";
import { isFetchingPosicheck } from "store/slices/DetectorPage/detectorSlice";
import { isErrorStatusCode, isSuccessfulStatusCode, isTimeoutStatusCode } from "utils/statusCheck";

import { Metrics } from "../DetectorDetail/useTopLevelStats";

export const useGetMetrics = (detectorId: string | undefined, refreshTime: number, count: number) => {
  const [histogramData, setHistogramData] = useState<HistogramEntry[]>([]);
  const isPosicheckLoading = useAppSelector(isFetchingPosicheck);

  const retryCountStats = useRef<number>(0);

  const { enqueueSnackbar } = useSnackbar();
  const { sendErrorEvent } = usePostEvent();

  const showErrorSnackbar = (response: Res<any>, sendError?: boolean) => {
    if (sendError) {
      sendErrorEvent(response.url);
      enqueueSnackbar("We're sorry, but an error has occurred. Please try reloading the page", {
        variant: "error",
      });
      return;
    }
    enqueueSnackbar(response.statusText || "We're sorry, but an error has occurred", { variant: "error" });
  };

  const { get: getMetrics, data: metrics } = useFetchCustom<Metrics>(
    `${JANZU_URL}device-api/metrics/detectors/${detectorId}?type=summary${""}`,
    {
      cachePolicy: CachePolicies.NO_CACHE,
      retries: NO_RETRIES,
      interceptors: {
        response: async ({ response }) => {
          const res = response;
          if (isSuccessfulStatusCode(res.status)) {
            retryCountStats.current = 0;
            return res;
          }

          if (isErrorStatusCode(res.status) && res.status !== 404) {
            showErrorSnackbar(response);
            return res;
          }

          if (isTimeoutStatusCode(res.status)) {
            retryCountStats.current += 1;
            if (retryCountStats.current === MAX_RETRIES) {
              showErrorSnackbar(response, true);
            }
          }
          return res;
        },
      },
    }
    /*  [detectorId] */
  );

  useEffect(() => {
    setHistogramData([]);
    if (detectorId) {
      getMetrics();
    }
  }, [detectorId, getMetrics]);

  // this useEffect is updating the data showed in the histogram everytime metrics change
  useEffect(() => {
    if (metrics && metrics.summary) {
      if (metrics.summary.confidence_histogram) {
        const data = metrics.summary.confidence_histogram.map((entry, index) => ({ ...entry, val: index }));
        setHistogramData(data);
      }
    } // eslint-disable-next-line
  }, [metrics /* stats */]);

  const updateData = useCallback(() => {
    if (detectorId) {
      getMetrics();
    }
  }, [detectorId, getMetrics]);

  useEffect(() => {
    if (count >= refreshTime && !isPosicheckLoading) {
      updateData();
    }
  }, [count, refreshTime, updateData, isPosicheckLoading]);

  return { histogramData, metrics };
};
